import { setAnalyticsConsent } from "../../lib/analytics";
import React, { useState, useEffect } from "react";
import { X, Cookie } from "lucide-react";

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);

  // Dependencies explanation:
  // ✅ Included: [] (empty - intentional, runs once on mount to check localStorage)
  // ❌ Not included: consent (local variable declared inside effect - doesn't exist in outer scope)
  // ❌ Not included: localStorage (Browser API - globally stable reference)
  // ❌ Not included: setIsVisible (React setState - guaranteed stable by React)
  // Why empty array? We want this to run ONCE on mount to check if user previously accepted cookies
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Check if user has already accepted cookies
    const consent = localStorage.getItem("nw_cookie_consent");
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);

  const acceptCookies = () => {
    setAnalyticsConsent(true);
    localStorage.setItem("nw_cookie_consent", "accepted");
    localStorage.setItem("nw_cookie_consent_date", new Date().toISOString());
    setIsVisible(false);
  };

  const declineCookies = () => {
    setAnalyticsConsent(false);
    localStorage.setItem("nw_cookie_consent", "declined");
    localStorage.setItem("nw_cookie_consent_date", new Date().toISOString());
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-4 bg-white border-t-2 border-sky-600 shadow-2xl animate-slide-up">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          {/* Icon and Text */}
          <div className="flex items-start gap-3 flex-1">
            <Cookie className="w-6 h-6 text-sky-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-slate-900 mb-1">Cookie Notice</h3>
              <p className="text-sm text-slate-600">
                We use essential cookies so the website works and remembers your preferences. With your permission we also
                use Google Analytics cookies to understand how visitors use the site. We do not use advertising cookies.
                Choose Accept to allow analytics or Decline to use essential cookies only. 
                Read our{" "}
                <a href="/privacy-policy" className="text-sky-600 hover:underline font-medium">
                  Privacy Policy
                </a>{" "}
                for more information.
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={declineCookies}
              data-testid="cookie-decline-btn"
              className="flex-1 md:flex-none px-6 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg transition-colors"
            >
              Decline
            </button>
            <button
              onClick={acceptCookies}
              data-testid="cookie-accept-btn"
              className="flex-1 md:flex-none px-6 py-2 text-sm font-medium bg-sky-600 hover:bg-sky-700 text-white rounded-lg transition-colors shadow-sm"
            >
              Accept
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CookieConsent;
